import { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { Field, Form } from "react-final-form";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { DatePicker } from "../../../common/fields";
import {
  defaultLicenseStart,
  formatLicenseDay,
  licenseErrorField,
  licenseErrorMessageId,
  toLicenseRequest,
  validateNewLicense,
} from "../../../../helpers/EnterpriseLicenseHelper";
import useCloseWhenSaved from "./useCloseWhenSaved";

// What was last sent, to tell whether the API's refusal is still about the
// dates on screen.
const requestJson = (values) =>
  values?.startDate && values?.endDate
    ? JSON.stringify(toLicenseRequest(values))
    : null;

// Marks every field touched, so an error shows under each of them.
const touchAll = (args, state) => {
  Object.values(state.fields).forEach((field) => {
    field.touched = true;
  });
};

// The form, mounted each time the dialog opens so it starts clean.
const AddLicenseForm = ({ latestEnd, saving, errorCode, onHide, onSubmit }) => {
  const intl = useIntl();
  const [sentJson, setSentJson] = useState(null);

  const labels = useMemo(
    () => ({
      startDate: intl.formatMessage({ id: "startDate" }),
      endDate: intl.formatMessage({ id: "endDate" }),
    }),
    [intl]
  );

  const messageOf = useCallback(
    (code, field) =>
      code === "required"
        ? intl.formatMessage({ id: "field_required" }, { label: labels[field] })
        : intl.formatMessage(
            { id: licenseErrorMessageId(code) },
            { date: formatLicenseDay(latestEnd) }
          ),
    [intl, labels, latestEnd]
  );

  // A refusal from the API reads the same as the check here, under the same
  // field, until a date changes.
  const serverCode =
    !saving && sentJson && licenseErrorMessageId(errorCode) ? errorCode : null;
  const serverField = serverCode && licenseErrorField(serverCode);

  const validate = useCallback(
    (values) => {
      const codes = validateNewLicense(values, { latestEnd });
      if (
        serverField &&
        !codes[serverField] &&
        requestJson(values) === sentJson
      ) {
        codes[serverField] = serverCode;
      }
      return Object.fromEntries(
        Object.entries(codes).map(([field, code]) => [
          field,
          messageOf(code, field),
        ])
      );
    },
    [latestEnd, serverField, serverCode, sentJson, messageOf]
  );

  const initialValues = useMemo(
    () => ({ startDate: defaultLicenseStart(latestEnd), endDate: null }),
    [latestEnd]
  );

  useCloseWhenSaved(saving, errorCode, onHide);

  const send = (values, form) => {
    // A refusal may be about a field the admin never touched (the start).
    form.mutators.touchAll();
    setSentJson(requestJson(values));
    onSubmit({ startDate: values.startDate, endDate: values.endDate });
  };

  return (
    <Form
      initialValues={initialValues}
      validate={validate}
      onSubmit={send}
      mutators={{ touchAll }}
      render={({ handleSubmit, values }) => (
        <form onSubmit={handleSubmit} noValidate data-testid="add-license-form">
          <p className="mt-0">
            {intl.formatMessage({ id: "add_license_intro" })}
          </p>
          <div className="grid">
            <div className="col-12 md:col-6">
              <Field
                name="startDate"
                component={DatePicker}
                label={labels.startDate}
                placeholder={labels.startDate}
                minDate={null}
                required
              />
            </div>
            <div className="col-12 md:col-6">
              <Field
                name="endDate"
                component={DatePicker}
                label={labels.endDate}
                placeholder={labels.endDate}
                minDate={null}
                required
              />
            </div>
          </div>
          {(serverCode && !serverField && requestJson(values) === sentJson && (
            <div
              className="edexlms-field-error"
              role="alert"
              data-testid="add-license-error"
            >
              {messageOf(serverCode)}
            </div>
          )) ||
            null}
          <div className="flex justify-content-end gap-2 mt-4">
            <Button
              type="button"
              outlined
              label={intl.formatMessage({ id: "cancel" })}
              onClick={onHide}
              disabled={saving}
              data-testid="add-license-cancel"
            />
            <Button
              type="submit"
              label={intl.formatMessage({ id: "add_license" })}
              loading={saving}
              disabled={saving}
              data-testid="add-license-submit"
            />
          </div>
        </form>
      )}
    />
  );
};

// Adds a licence; a licence is never edited, a new one is added instead.
const AddLicenseDialog = ({ visible, onHide, ...formProps }) => {
  const intl = useIntl();
  // No close icon or Escape while saving: a refusal the form explains is not
  // a toast, so closing before the API answers would lose it.
  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      closable={!formProps.saving}
      closeOnEscape={!formProps.saving}
      header={intl.formatMessage({ id: "add_license" })}
      style={{ width: "36rem" }}
      breakpoints={{ "640px": "calc(100vw - 2rem)" }}
    >
      {(visible && <AddLicenseForm onHide={onHide} {...formProps} />) || null}
    </Dialog>
  );
};

AddLicenseForm.propTypes = {
  latestEnd: PropTypes.instanceOf(Date),
  saving: PropTypes.bool,
  errorCode: PropTypes.string,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

AddLicenseDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  latestEnd: PropTypes.instanceOf(Date),
  saving: PropTypes.bool,
  errorCode: PropTypes.string,
};

export default AddLicenseDialog;
